import React, { useState, useEffect } from 'react';
import Modal from '../../../../../../../../components/Modal';
import Icon from '../../../../../../../../components/Icon';
import LogoLoader from '../../../../../../../../components/LogoLoader';
import { useTheme } from 'styled-components';
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import fullscreenIcon from '../../../../../../../../assets/icons/size.svg';
import collapseIcon from '../../../../../../../../assets/icons/collapse.svg';
import downloadIcon from '../../../../../../../../assets/icons/download-file.svg';
import {
  ImageModalFullscreenStyles,
  ModalContent,
  MediaContainer,
  VideoElement,
  ImageElement,
  ButtonsContainer,
  ActionButton
} from './styles';
import constants from '../../../../../../../../constants';

interface ImageModalProps {
  files: Array<{
    id?: number;
    name?: string;
    original_name?: string;
    url?: string;
    signed_url?: string;
    mime?: string;
  }>;
  initialIndex: number;
  visibleModal?: string | null;
  isDownloadable?: boolean;
}

const ImageModal: React.FC<ImageModalProps> = ({ files, initialIndex, visibleModal, isDownloadable = true }) => {
  const theme = useTheme() as { colors: { [key: string]: string } };
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [modalWidth, setModalWidth] = useState<number>(720);
  const [aspectRatio, setAspectRatio] = useState<number>(16 / 9);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const handle = useFullScreenHandle();

  // Update current index when initialIndex changes
  useEffect(() => {
    console.log('🎬 Modal Index Update:', {
      initialIndex,
      filesCount: files.length,
      files: files.map((f, i) => ({ index: i, name: f.name || f.original_name, mime: f.mime }))
    });
    setCurrentIndex(initialIndex);
  }, [initialIndex, files]);

  // Reset modal width when modal closes
  useEffect(() => {
    if (!visibleModal) {
      setModalWidth(720); // Reset to default width
      setAspectRatio(16 / 9); // Reset to default ratio
      setIsLoading(true); // Reset loading state
    }
  }, [visibleModal]);

  // Calculate modal width based on media dimensions
  useEffect(() => {
    const selectedFile = files[currentIndex];
    if (!selectedFile || !visibleModal) return; // Solo calcular si el modal está visible

    const isVideo = constants.fileUploader.videoMimes.includes(selectedFile.mime || '');
    const isImage = constants.fileUploader.imagesMimes.includes(selectedFile.mime || '');

    // Reset width first to avoid flicker
    setModalWidth(720);
    setAspectRatio(16 / 9);
    setIsLoading(true);

    // Pequeño delay para asegurar que el modal esté renderizado
    const timeoutId = setTimeout(() => {
      if (isVideo) {
        // Para videos, crear un elemento video temporal para obtener dimensiones
        const video = document.createElement('video');
        video.src = selectedFile.signed_url || selectedFile.url || '';
        video.addEventListener('loadedmetadata', () => {
          const ratio = video.videoWidth / video.videoHeight;
          setAspectRatio(ratio);
          // Si es portrait (ratio < 1) usa 360px, si es wide usa 720px
          setModalWidth(ratio < 1 ? 360 : 720);
          setIsLoading(false); // Finalizar loading
        });
      } else if (isImage) {
        // Para imágenes, crear un elemento img temporal para obtener dimensiones
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Evitar problemas de CORS
        img.src = selectedFile.signed_url || selectedFile.url || '';

        img.onload = () => {
          const ratio = img.width / img.height;
          setAspectRatio(ratio);
          // Si es portrait (ratio < 1) usa 360px, si es wide usa 720px
          setModalWidth(ratio < 1 ? 360 : 720);
          setIsLoading(false); // Finalizar loading
        };

        img.onerror = () => {
          // Si falla la carga, usar valores por defecto
          setModalWidth(720);
          setAspectRatio(16 / 9);
          setIsLoading(false); // Finalizar loading
        };
      }
    }, 100); // 100ms delay

    return () => clearTimeout(timeoutId);
  }, [currentIndex, files, visibleModal]);

  const enterFullScreen = async () => {
    try {
      await handle.enter();
      setIsFullScreen(true);
    } catch (error) {
      console.error("Error entering full-screen mode:", error);
    }
  };

  const exitFullScreen = async () => {
    try {
      await handle.exit();
      setIsFullScreen(false);
    } catch (error) {
      console.error("Error exiting full-screen mode:", error);
    }
  };

  const selectedFile = files[currentIndex];
  const isVideo = selectedFile && constants.fileUploader.videoMimes.includes(selectedFile.mime || '');
  const isImage = selectedFile && constants.fileUploader.imagesMimes.includes(selectedFile.mime || '');

  if (!selectedFile) return null;

  return (
    <>
      <ImageModalFullscreenStyles />
      <Modal
        name="complementary-files-modal"
        visibleModal={visibleModal}
        width={modalWidth.toString()}
        styles={{
          modalContent: {
            maxHeight: '100svh',
            padding: '0px',
            backgroundColor: "transparent"
          },
          modalHeader: {
            display: 'none'
          }
        }}
        title={selectedFile.name || selectedFile.original_name || "Material Complementario"}
      >
        <ModalContent>
          {isLoading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              gap: '16px'
            }}>
              <LogoLoader size={60} />
              <div style={{
                fontSize: '14px',
                color: '#666',
                fontWeight: 500
              }}>
                Cargando...
              </div>
            </div>
          ) : (
            <FullScreen
              handle={handle}
              onChange={(state, handle) => {
                if (state) {
                  setIsFullScreen(true);
                } else {
                  setIsFullScreen(false);
                }
              }}
              className={`image-modal-wrapper ${isFullScreen ? 'fullscreen-mode' : ''}`}
            >
              <MediaContainer $isFullScreen={isFullScreen}>
                {isVideo ? (
                  <VideoElement
                    src={selectedFile.signed_url || selectedFile.url}
                    controls
                    controlsList={!isDownloadable ? "nodownload" : undefined}
                    onContextMenu={(e) => {
                      if (!isDownloadable) {
                        e.preventDefault();
                        return false;
                      }
                    }}
                  />
                ) : (
                  <div style={{ position: 'relative', display: 'flex', width: '100%', height: '100%' }}>
                    <ImageElement
                      src={selectedFile.signed_url || selectedFile.url}
                      alt={selectedFile.name || selectedFile.original_name || 'Archivo'}
                      onContextMenu={(e) => {
                        if (!isDownloadable) {
                          e.preventDefault();
                          return false;
                        }
                      }}
                      onDragStart={(e) => {
                        if (!isDownloadable) {
                          e.preventDefault();
                          return false;
                        }
                      }}
                      draggable={isDownloadable}
                      style={{
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none'
                      }}
                    />
                    {/* Capa de protección sobre la imagen cuando no es descargable */}
                    {!isDownloadable && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 1,
                          cursor: 'default'
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          return false;
                        }}
                        onDragStart={(e) => {
                          e.preventDefault();
                          return false;
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Botones flotantes - siempre por encima de la capa de protección */}
                <ButtonsContainer style={{ zIndex: 2 }}>
                  {/* Botón de descarga - solo para imágenes */}
                  {isDownloadable && !isVideo && (
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        const link = document.createElement('a');
                        link.href = selectedFile.signed_url || selectedFile.url || '';
                        link.download = selectedFile.name || selectedFile.original_name || 'download';
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      <Icon
                        icon={downloadIcon}
                        size="24px"
                        color="#000000"
                      />
                    </ActionButton>
                  )}

                  {/* Botón fullscreen - solo para imágenes */}
                  {!isVideo && (
                    <ActionButton onClick={isFullScreen ? exitFullScreen : enterFullScreen}>
                      <Icon
                        icon={isFullScreen ? collapseIcon : fullscreenIcon}
                        size="24px"
                        color="#000000"
                      />
                    </ActionButton>
                  )}
                </ButtonsContainer>
              </MediaContainer>
            </FullScreen>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default ImageModal;
